import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Download, ChevronDown, X, Search, Users } from 'lucide-react';
import Chart from 'react-apexcharts';
import { Button } from '../../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../components/ui/card';
import { cn } from '../../../../../lib/utils';
import useEmployeesActivity from '../../hooks/useEmployeesActivity';
import useEmployeesData from '../../hooks/useEmployeesData';

const SERIES_CONFIG = {
  LINK_CLICKED: { name: 'Link Clicked', color: '#8B5CF6' },
  DONE: { name: 'Done', color: '#10B981' },
  ON_HOLD: { name: 'On Hold', color: '#F59E0B' },
  IN_PROGRESS: { name: 'In Progress', color: '#3B82F6' },
};

function EmployeeMultiSelect({ options, selected, onChange, onSearch, placeholder }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    onSearch(e);
  };

  const handleSelect = (option) => {
    const isSelected = selected.some((s) => s.id === option.id);
    if (isSelected) {
      onChange(selected.filter((s) => s.id !== option.id));
    } else {
      onChange([...selected, option]);
    }
  };

  const handleRemove = (option) => {
    onChange(selected.filter((s) => s.id !== option.id));
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center justify-between w-full px-3 py-2 text-sm rounded-lg',
          'bg-background border border-input',
          'hover:bg-accent',
          'focus:outline-none focus:ring-2 focus:ring-ring',
        )}
      >
        <span className="text-muted-foreground">
          {selected.length > 0 ? `${selected.length} selected` : placeholder}
        </span>
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </button>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selected.map((item) => (
            <span
              key={item.id}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-primary/10 text-primary"
            >
              {item.email}
              <button
                type="button"
                onClick={() => handleRemove(item)}
                className="hover:text-primary/70"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by email (min 3 chars)..."
                value={search}
                onChange={handleSearchChange}
                className={cn(
                  'w-full pl-8 pr-2 py-1.5 text-sm rounded',
                  'bg-background border border-input',
                  'focus:outline-none focus:ring-1 focus:ring-ring',
                )}
              />
            </div>
          </div>
          <div className="max-h-48 overflow-auto p-1">
            {options.length === 0 ? (
              <p className="px-2 py-3 text-sm text-muted-foreground text-center">
                {search.length < 3 ? 'Type at least 3 characters to search' : 'No results found'}
              </p>
            ) : (
              options.map((option) => {
                const isSelected = selected.some((s) => s.id === option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={cn(
                      'w-full text-left px-2 py-1.5 text-sm rounded',
                      'hover:bg-accent',
                      isSelected && 'bg-primary/10 text-primary',
                    )}
                  >
                    {option.email}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

EmployeeMultiSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selected: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

EmployeeMultiSelect.defaultProps = {
  placeholder: 'Select...',
};

function EmployeesActivityChart({ startDate, endDate }) {
  const { employeesOptions, pickedEmployees, fetchEmployees, setPickedEmployees } =
    useEmployeesData();

  const { employeesChartOptions, employeesChartSeries } = useEmployeesActivity({
    startPickerValue: startDate,
    endPickerValue: endDate,
    pickedEmployees,
  });

  const exportUrl = new URL(
    '/api/v1/users-statistic/admin/export/users',
    process.env.REACT_APP_API_URL,
  );
  exportUrl.searchParams.set('startDate', startDate.toISOString());
  exportUrl.searchParams.set('endDate', endDate.toISOString());
  exportUrl.searchParams.set('userIds', pickedEmployees.map((e) => e.id).join(','));

  // Map series to friendly names
  const formattedSeries = useMemo(() => {
    return employeesChartSeries.map((s) => ({
      ...s,
      name: SERIES_CONFIG[s.name]?.name || s.name,
    }));
  }, [employeesChartSeries]);

  // Customize chart options for our theme
  const themedChartOptions = useMemo(() => {
    return {
      ...employeesChartOptions,
      chart: {
        ...employeesChartOptions.chart,
        toolbar: { show: false },
        fontFamily: 'inherit',
        background: 'transparent',
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 400,
        },
      },
      colors: [
        SERIES_CONFIG.LINK_CLICKED.color,
        SERIES_CONFIG.DONE.color,
        SERIES_CONFIG.ON_HOLD.color,
        SERIES_CONFIG.IN_PROGRESS.color,
      ],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
          borderRadius: 4,
          borderRadiusApplication: 'end',
          borderRadiusWhenStacked: 'last',
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      grid: {
        borderColor: 'hsl(var(--border))',
        strokeDashArray: 4,
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 10,
        },
      },
      xaxis: {
        ...employeesChartOptions.xaxis,
        labels: {
          ...employeesChartOptions.xaxis?.labels,
          style: {
            colors: 'hsl(var(--muted-foreground))',
            fontSize: '11px',
            fontWeight: 500,
          },
          rotate: -45,
          rotateAlways: false,
        },
        axisBorder: {
          show: true,
          color: 'hsl(var(--border))',
        },
        axisTicks: {
          show: true,
          color: 'hsl(var(--border))',
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: 'hsl(var(--muted-foreground))',
            fontSize: '11px',
            fontWeight: 500,
          },
          formatter: (val) => Math.round(val),
        },
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        floating: false,
        fontSize: '12px',
        fontWeight: 500,
        labels: {
          colors: 'hsl(var(--foreground))',
        },
        markers: {
          size: 8,
          shape: 'circle',
          offsetX: -4,
        },
        itemMargin: {
          horizontal: 12,
          vertical: 0,
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (val) => `${val} tasks`,
        },
        style: {
          fontSize: '12px',
        },
        marker: {
          show: true,
        },
      },
      fill: {
        opacity: 1,
      },
      states: {
        hover: {
          filter: {
            type: 'darken',
            value: 0.9,
          },
        },
        active: {
          filter: {
            type: 'darken',
            value: 0.85,
          },
        },
      },
    };
  }, [employeesChartOptions]);

  // Calculate totals for stats
  const totals = useMemo(() => {
    const result = { linkClicked: 0, done: 0, onHold: 0, inProgress: 0 };
    employeesChartSeries.forEach((s) => {
      const sum = s.data.reduce((acc, val) => acc + val, 0);
      if (s.name === 'LINK_CLICKED') result.linkClicked = sum;
      if (s.name === 'DONE') result.done = sum;
      if (s.name === 'ON_HOLD') result.onHold = sum;
      if (s.name === 'IN_PROGRESS') result.inProgress = sum;
    });
    return result;
  }, [employeesChartSeries]);

  return (
    <Card style={{ border: '1px solid hsl(var(--border))' }}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold">Employees Activity</CardTitle>
              <p className="text-sm text-muted-foreground">
                Track employee task progress over time
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" asChild>
            <a href={exportUrl.toString()} download>
              <Download className="h-4 w-4 mr-2" />
              Export
            </a>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
            <div className="text-lg font-bold text-purple-600">{totals.linkClicked}</div>
            <div className="text-xs text-muted-foreground">Links Clicked</div>
          </div>
          <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
            <div className="text-lg font-bold text-green-600">{totals.done}</div>
            <div className="text-xs text-muted-foreground">Done</div>
          </div>
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="text-lg font-bold text-amber-600">{totals.onHold}</div>
            <div className="text-xs text-muted-foreground">On Hold</div>
          </div>
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <div className="text-lg font-bold text-blue-600">{totals.inProgress}</div>
            <div className="text-xs text-muted-foreground">In Progress</div>
          </div>
        </div>

        {/* Employee Filter */}
        <div className="pt-2">
          <EmployeeMultiSelect
            options={employeesOptions}
            selected={pickedEmployees}
            onChange={setPickedEmployees}
            onSearch={fetchEmployees}
            placeholder="Filter by employees..."
          />
        </div>

        {/* Chart */}
        <div className="h-[320px] pt-2">
          {formattedSeries.length > 0 ? (
            <Chart options={themedChartOptions} series={formattedSeries} type="bar" height="100%" />
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              Loading chart data...
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

EmployeesActivityChart.propTypes = {
  startDate: PropTypes.instanceOf(dayjs).isRequired,
  endDate: PropTypes.instanceOf(dayjs).isRequired,
};

export default EmployeesActivityChart;
